import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Input } from './Input.tsx';
import { Select } from './Select.tsx';
import { Badge } from './Badge.tsx';
import { Button } from './Button.tsx';
import { Icon } from './Icon.tsx';
import { useRoster } from '../../features/admin/hooks/useRoster.ts';
import type { RosterPerson, RosterCategory } from '../../features/admin/org-types.ts';
import type { Role } from '../../features/auth/types.ts';

const NativeButton = 'button';

// ============================================================================
// RosterSearch — Typeahead search-and-select from org roster
// ============================================================================
// Shared component used by GettingStartedWizard and BatchOnboardingComposer.
// Features:
//   - Typeahead input → client-side filter by name/email
//   - Selected invitees displayed as removable chips
//   - Optional app access assignment per invitee
//   - Manual entry fallback for people not in roster

export interface SelectedInvitee {
    /** Unique key for React rendering */
    id: string;
    name: string;
    email: string;
    roleId?: string;
    roleName?: string;
    category: RosterCategory | 'cast' | 'crew' | 'production';
    /** If sourced from roster */
    rosterPersonId?: string;
}

export interface RosterSearchProps {
    orgId: string | null;
    selectedInvitees: SelectedInvitee[];
    onChange: (invitees: SelectedInvitee[]) => void;
    roles?: Role[];
    /** Placeholder text for the search input */
    placeholder?: string;
    /** Max height for the dropdown */
    maxDropdownHeight?: string;
}

const CATEGORY_COLORS: Record<string, 'primary' | 'success' | 'indigo' | 'teal'> = {
    cast: 'primary',
    crew: 'teal',
    production: 'indigo',
};

export const RosterSearch: React.FC<RosterSearchProps> = ({
    orgId,
    selectedInvitees,
    onChange,
    roles,
    placeholder = 'Search roster by name or email...',
    maxDropdownHeight = '240px',
}) => {
    const { people, loading } = useRoster(orgId);
    const showRolePicker = roles !== undefined;
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [showManualForm, setShowManualForm] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Manual entry state
    const [manualName, setManualName] = useState('');
    const [manualEmail, setManualEmail] = useState('');
    const [manualRoleId, setManualRoleId] = useState('');
    const [manualCategory, setManualCategory] = useState<'cast' | 'crew' | 'production'>('crew');

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter roster — exclude already selected, match by name or email
    const selectedIds = useMemo(
        () => new Set(selectedInvitees.map((i) => i.rosterPersonId).filter(Boolean)),
        [selectedInvitees]
    );

    const filtered = useMemo(() => {
        if (!query.trim()) return [];
        const q = query.toLowerCase();
        return people
            .filter((p) => !selectedIds.has(p.id))
            .filter(
                (p) =>
                    p.name.toLowerCase().includes(q) ||
                    p.email?.toLowerCase().includes(q)
            )
            .slice(0, 20); // Cap results for perf
    }, [people, query, selectedIds]);

    const handleSelect = useCallback(
        (person: RosterPerson) => {
            const invitee: SelectedInvitee = {
                id: crypto.randomUUID(),
                name: person.name,
                email: person.email || '',
                roleId: showRolePicker ? '' : undefined,
                roleName: showRolePicker ? '' : undefined,
                category: person.category,
                rosterPersonId: person.id,
            };
            onChange([...selectedInvitees, invitee]);
            setQuery('');
            setIsOpen(false);
            inputRef.current?.focus();
        },
        [selectedInvitees, onChange, showRolePicker]
    );

    const handleRemove = useCallback(
        (id: string) => {
            onChange(selectedInvitees.filter((i) => i.id !== id));
        },
        [selectedInvitees, onChange]
    );

    const handleRoleChange = useCallback(
        (id: string, roleId: string) => {
            const role = roles?.find((r) => r.id === roleId);
            onChange(
                selectedInvitees.map((i) =>
                    i.id === id ? { ...i, roleId, roleName: role?.name || '' } : i
                )
            );
        },
        [selectedInvitees, onChange, roles]
    );

    const handleAddManual = useCallback(() => {
        if (!manualName.trim() || !manualEmail.trim()) return;
        const role = roles?.find((r) => r.id === manualRoleId);
        const invitee: SelectedInvitee = {
            id: crypto.randomUUID(),
            name: manualName.trim(),
            email: manualEmail.trim(),
            roleId: showRolePicker ? manualRoleId : undefined,
            roleName: showRolePicker ? role?.name || '' : undefined,
            category: manualCategory,
        };
        onChange([...selectedInvitees, invitee]);
        setManualName('');
        setManualEmail('');
        setManualRoleId('');
        setManualCategory('crew');
        setShowManualForm(false);
    }, [
        manualName,
        manualEmail,
        manualRoleId,
        manualCategory,
        selectedInvitees,
        onChange,
        roles,
        showRolePicker,
    ]);

    const roleOptions = useMemo(
        () => [
            { value: '', label: 'App access...' },
            ...(roles ?? [])
                .filter((r) => r.id !== 'pending')
                .map((r) => ({ value: r.id, label: r.name })),
        ],
        [roles]
    );

    const categoryOptions = [
        { value: 'cast', label: 'Cast' },
        { value: 'crew', label: 'Crew' },
        { value: 'production', label: 'Production' },
    ];

    return (
        <div className="space-y-3" ref={containerRef}>
            {/* Search Input */}
            <div className="relative">
                <Input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(e.target.value.trim().length > 0);
                    }}
                    onFocus={() => query.trim() && setIsOpen(true)}
                    placeholder={loading ? 'Loading roster...' : placeholder}
                    leftIcon="search"
                    fullWidth
                    disabled={loading}
                />

                {/* Dropdown */}
                {isOpen && filtered.length > 0 ? <div
                        className="absolute z-20 mt-1 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-surface-dark"
                        style={{ maxHeight: maxDropdownHeight }}
                    >
                        {filtered.map((person) => (
                            <NativeButton
                                type="button"
                                key={person.id}
                                onClick={() => handleSelect(person)}
                                className="flex w-full items-center gap-3 border-gray-100 border-b px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="truncate font-medium text-gray-900 text-sm dark:text-white">
                                        {person.name}
                                    </p>
                                    {person.email ? <p className="truncate text-gray-500 text-xs dark:text-gray-400">
                                            {person.email}
                                        </p> : null}
                                </div>
                                <Badge variant={CATEGORY_COLORS[person.category] || 'primary'}>
                                    {person.category}
                                </Badge>
                            </NativeButton>
                        ))}
                    </div> : null}

                {/* No results message */}
                {isOpen && query.trim().length > 1 && filtered.length === 0 && !loading ? <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white p-4 text-center shadow-lg dark:border-gray-700 dark:bg-surface-dark">
                        <p className="text-gray-500 text-sm dark:text-gray-400">
                            No roster matches for <span className="font-medium">{query}</span>
                        </p>
                        <NativeButton
                            type="button"
                            onClick={() => {
                                setShowManualForm(true);
                                setManualName(query);
                                setIsOpen(false);
                                setQuery('');
                            }}
                            className="mt-1 text-primary text-sm hover:underline"
                        >
                            Add manually instead
                        </NativeButton>
                    </div> : null}
            </div>

            {/* Selected Invitees */}
            {selectedInvitees.length > 0 && (
                <div className="space-y-2">
                    <p className="font-medium text-gray-500 text-xs uppercase tracking-wider dark:text-gray-400">
                        {selectedInvitees.length} selected
                    </p>
                    <div className="max-h-60 space-y-1.5 overflow-y-auto pr-1">
                        {selectedInvitees.map((invitee) => (
                            <div
                                key={invitee.id}
                                className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/50"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="truncate font-medium text-gray-900 text-sm dark:text-white">
                                            {invitee.name}
                                        </span>
                                        <Badge
                                            variant={
                                                CATEGORY_COLORS[invitee.category] || 'primary'
                                            }
                                        >
                                            {invitee.category}
                                        </Badge>
                                    </div>
                                    <p className="truncate text-gray-500 text-xs dark:text-gray-400">
                                        {invitee.email || 'No email'}
                                    </p>
                                </div>
                                {showRolePicker ? <Select
                                        value={invitee.roleId || ''}
                                        onChange={(e) =>
                                            handleRoleChange(invitee.id, e.target.value)
                                        }
                                        options={roleOptions}
                                        className="w-36 text-xs"
                                    /> : null}
                                <NativeButton
                                    type="button"
                                    onClick={() => handleRemove(invitee.id)}
                                    className="rounded p-1 text-gray-400 transition-colors hover:text-red-500"
                                    title="Remove"
                                >
                                    <Icon name="close" size="sm" />
                                </NativeButton>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Manual Entry Fallback */}
            {showManualForm ? (
                <div className="animate-fadeIn space-y-3 rounded-lg border border-gray-300 border-dashed p-3 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-500 text-xs uppercase tracking-wider dark:text-gray-400">
                            Add someone not in the roster
                        </p>
                        <NativeButton
                            type="button"
                            onClick={() => setShowManualForm(false)}
                            className="text-gray-400 text-xs transition-colors hover:text-gray-600"
                        >
                            Cancel
                        </NativeButton>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Input
                            value={manualName}
                            onChange={(e) => setManualName(e.target.value)}
                            placeholder="Full name"
                            fullWidth
                        />
                        <Input
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            placeholder="email@example.com"
                            type="email"
                            fullWidth
                        />
                    </div>
                    <div className={showRolePicker ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1 gap-2'}>
                        {showRolePicker ? <Select
                                value={manualRoleId}
                                onChange={(e) => setManualRoleId(e.target.value)}
                                options={roleOptions}
                            /> : null}
                        <Select
                            value={manualCategory}
                            onChange={(e) =>
                                setManualCategory(
                                    e.target.value as 'cast' | 'crew' | 'production'
                                )
                            }
                            options={categoryOptions}
                        />
                    </div>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleAddManual}
                        disabled={!manualName.trim() || !manualEmail.trim()}
                        leftIcon="person_add"
                    >
                        Add Person
                    </Button>
                </div>
            ) : (
                <NativeButton
                    type="button"
                    onClick={() => setShowManualForm(true)}
                    className="flex items-center gap-1.5 text-gray-500 text-xs transition-colors hover:text-primary"
                >
                    <Icon name="person_add" size="sm" />
                    Add someone not in the roster
                </NativeButton>
            )}
        </div>
    );
};

RosterSearch.displayName = 'RosterSearch';
